import React, { useEffect, useState, useRef } from 'react';
import {
  Paper as MuiPaper,
  Box as MuiBox,
  List,
  ListItem as MuiListItem,
  ListItemIcon as MuiListItemIcon,
  ListItemText as MuiListItemText,
  styled,
  CircularProgress,
  useTheme,
  Theme,
  Tooltip,
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';
import FolderOffIcon from '@mui/icons-material/FolderOff';
import {
  BackgroundExportStatus,
  BackgroundExportTypes,
} from 'further-types/background-exports';
import { DropdownArrow, SpreadsheetIcon } from '../SVGIcons';
import getDownloadTitle from './get-download-title';
import { CalendarTime } from 'utils';

const Box = styled(MuiBox)<{ borderColor?: string }>(
  ({ theme, borderColor }) => ({
    position: 'fixed',
    bottom: 0,
    right: '3rem',
    zIndex: 2000,
    overflow: 'hidden',
    border: `2px solid ${borderColor}`,
    borderRadius: '1rem 1rem 0 0',
    borderBottom: 'none',
    maxHeight: 'calc(100vh - 2rem)',
    overflowY: 'auto',
    width: '455px',
    [theme.breakpoints.down('sm')]: {
      right: '1rem',
      width: 'unset',
      maxWidth: 'calc(100vw - 2rem)',
    },
  }),
);

const Paper = styled(MuiPaper)({
  borderRadius: 0,
});

const Spinner = styled(CircularProgress)({
  color: 'rgba(0, 0, 0, 0.26)',
  marginRight: '1px',
});

const MyDownloadsRow = styled('div')<{ borderColor?: string }>(
  ({ theme, borderColor }: { theme: Theme; borderColor?: string }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: `2px solid ${borderColor}`,
    cursor: 'pointer',
    padding: '1.125rem',
    minWidth: '340px',
    [theme.breakpoints.down('sm')]: {
      minWidth: 'unset',
    },
  }),
);

interface ListItemProps {
  borderColor?: string;
  isCompleted: boolean;
  disabled: boolean;
}

interface ListItemTextProps {
  isCompleted: boolean;
}

interface RotatingDivProps {
  isExpanded: boolean;
}

const ListItem = styled(MuiListItem, {
  shouldForwardProp: (prop: PropertyKey) =>
    !['isCompleted', 'borderColor'].includes(prop as string),
})<ListItemProps>(({ theme, borderColor, isCompleted, disabled }) => ({
  padding: '0.75rem',
  borderBottom: `2px solid ${borderColor}`,
  cursor: disabled ? 'default' : isCompleted ? 'pointer' : 'default',
  '&:hover': {
    backgroundColor: disabled
      ? 'inherit'
      : isCompleted
      ? theme.palette.action.hover
      : 'inherit',
  },
}));

const ListItemIcon = styled(MuiListItemIcon)({
  minWidth: '20px',
  marginRight: '0.75rem',
});

const ListItemText = styled(MuiListItemText, {
  shouldForwardProp: (prop: PropertyKey) =>
    !['isCompleted'].includes(prop as string),
})<ListItemTextProps>(({ theme, isCompleted }) => ({
  marginTop: '4px',
  marginBottom: '4px',
  marginRight: '4rem',
  color: isCompleted ? 'inherit' : theme.palette.grey[400],
  lineHeight: '24px',
}));

const RotatingDiv = styled('div')<RotatingDivProps>(({ isExpanded }) => ({
  transform: !isExpanded ? 'rotate(180deg)' : 'none',
}));

const DownloadIcon = styled(GetAppIcon)({
  fontSize: '24px',
});

const CloseIconButton = styled(CloseIcon)(({ theme }) => ({
  fontSize: '24px',
  padding: '2px',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderRadius: '50%',
  },
}));

const ActionsContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  paddingLeft: '1rem',
});

const DropdownArrowButton = styled(DropdownArrow)(({ theme }) => ({
  fontSize: '24px',
  padding: '2px',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderRadius: '50%',
  },
}));

const ItemActionsContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginLeft: 'auto',
});

const ItemCloseIconButton = styled(CloseIcon)(({ theme }) => ({
  fontSize: '24px',
  padding: '2px',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    borderRadius: '50%',
  },
}));

type Params = {
  iconColor?: string;
  borderColor?: string;
  backgroundExports: Array<{
    _id: string;
    exportType?: BackgroundExportTypes;
    status?: BackgroundExportStatus;
    createdAt?: CalendarTime;
    downloaded?: boolean;
  }>;
  downloadBackgroundExport: (exportId: string) => void;
  deleteBackgroundExport: (exportId: string) => void;
  Heading?: React.ReactNode;
};

const DownloadTray: React.FC<Params> = ({
  iconColor,
  borderColor,
  backgroundExports,
  downloadBackgroundExport,
  deleteBackgroundExport,
  Heading,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [downloadClicked, setDownloadClicked] = useState<
    Record<string, boolean>
  >({});
  const [hidden, setHidden] = useState(false);
  const theme = useTheme();
  const prevExportsLength = useRef(backgroundExports.length);

  const allDownloadsCompleted = backgroundExports.every(
    (exportItem) =>
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      exportItem.downloaded ||
      downloadClicked[exportItem._id] ||
      exportItem.status === BackgroundExportStatus.Failed ||
      exportItem.status === BackgroundExportStatus.NoData,
  );

  useEffect(() => {
    if (
      !allDownloadsCompleted &&
      backgroundExports.length > prevExportsLength.current
    ) {
      setIsExpanded(true);
      setHidden(false);
    }
    prevExportsLength.current = backgroundExports.length;
  }, [backgroundExports.length, allDownloadsCompleted]);

  if (hidden) return null;

  return (
    <Box borderColor={borderColor}>
      <Paper elevation={3}>
        <MyDownloadsRow
          borderColor={borderColor}
          onClick={() => {
            setIsExpanded(!isExpanded);
          }}
        >
          {Heading ?? null}

          <ActionsContainer>
            <CloseIconButton
              aria-label="close"
              onClick={(e) => {
                e.stopPropagation();
                setHidden(true);
              }}
              color="error"
            />
            <RotatingDiv isExpanded={isExpanded}>
              <span id="download-tray-dropdown-arrow">
                <DropdownArrowButton color="primary" />
              </span>
            </RotatingDiv>
          </ActionsContainer>
        </MyDownloadsRow>

        {isExpanded ? (
          <List disablePadding sx={{ gap: 0 }}>
            {backgroundExports.map((item) => {
              const isPending = item.status === BackgroundExportStatus.Pending;
              const isCompleted =
                item.status === BackgroundExportStatus.Completed;
              const isFailed = item.status === BackgroundExportStatus.Failed;
              const isNoData = item.status === BackgroundExportStatus.NoData;
              return (
                <ListItem
                  borderColor={borderColor}
                  isCompleted={isCompleted}
                  key={item._id}
                  onClick={() => {
                    if (isCompleted && item._id) {
                      setDownloadClicked((prev) => ({
                        ...prev,
                        [item._id ?? '']: true,
                      }));
                      downloadBackgroundExport(item._id);
                    }
                  }}
                  disabled={!isCompleted}
                >
                  <ListItemIcon>
                    <SpreadsheetIcon
                      size={22}
                      fill={
                        isCompleted || isFailed || isNoData
                          ? iconColor
                          : theme.palette.grey[300]
                      }
                    />
                  </ListItemIcon>
                  <ListItemText
                    isCompleted={isCompleted}
                    primary={getDownloadTitle(item)}
                    slotProps={{
                      primary: {
                        paddingBottom: '0',
                        paddingTop: '4px',
                        fontSize: '16px',
                        color:
                          isCompleted || isFailed || isNoData
                            ? theme.palette.grey[900]
                            : theme.palette.grey[400],
                      },
                    }}
                  />

                  <ItemActionsContainer>
                    <ItemCloseIconButton
                      aria-label="remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (item._id) {
                          deleteBackgroundExport(item._id);
                        }
                      }}
                      color="error"
                    />
                    {isPending ? <Spinner size={20} /> : null}
                    {isCompleted ? (
                      <DownloadIcon
                        htmlColor={
                          !item.downloaded && !downloadClicked[item._id ?? '']
                            ? iconColor
                            : 'disabled'
                        }
                      />
                    ) : null}
                    {isFailed ? <ErrorIcon color="error" /> : null}
                    {isNoData ? (
                      <Tooltip
                        title="No data was available for this export"
                        placement="top"
                        slotProps={{
                          popper: {
                            style: {
                              zIndex: 2010,
                            },
                          },
                        }}
                      >
                        <FolderOffIcon color="error" />
                      </Tooltip>
                    ) : null}
                  </ItemActionsContainer>
                </ListItem>
              );
            })}
          </List>
        ) : null}
      </Paper>
    </Box>
  );
};

export default DownloadTray;
